import React, { useEffect, useState } from "react";
import { useTaskCreation } from "../../../hooks/useTaskCreation";
import { IOptionProps, IOptionsProps } from "../../../@types/reactSelect/IOption";
import { DropdownV2, CounterInputV2 } from "../../FormControlsV2";
import axios from "../../../services/axios";
import "./style.css";
import { MessageBalloon } from "../../MessageBalloon/Index";

interface PlantingProps { }

interface IFodderSpecie {
  id: number;
  genus_name: string;
  fodder_types: IFodderType[];
}

interface IFodderType {
  fodder_species: string;
  fodder_species_id: number;
}

export const Planting: React.FC<PlantingProps> = () => {
  const {
    payload,
    setPayload,
    setAllowTaskCreation,
    responsibleId,
    microAreaId,
  } = useTaskCreation();

  const [plantingTypes, setPlantingTypes] = useState<IOptionsProps>([]);
  const [fodderSpecies, setFodderSpecies] = useState<IOptionsProps>([]);
  const [fodderTypes, setFodderTypes] = useState<IOptionsProps>([]);
  const [seedTypes, setSeedTypes] = useState<IOptionsProps>([]);

  const [selectedPlantingType, setSelectedPlantingType] = useState<IOptionProps | null>(null);
  const [selectedFodderSpecies, setSelectedFodderSpecies] = useState<IOptionProps | null>(null);
  const [selectedFodderType, setSelectedFodderType] = useState<IOptionProps | null>(null);
  const [selectedSeedType, setSelectedSeedType] = useState<IOptionProps | null>(null);

  const [showSpecies, setShowSpecies] = useState<boolean>(false);
  const [showTypes, setShowTypes] = useState<boolean>(false);
  const [showSeedTypes, setShowSeedTypes] = useState<boolean>(false);
  const [showCounters, setShowCounters] = useState<boolean>(false);

  const [vcPercentage, setVcPercentage] = useState<number>(0);
  const [seedWeight, setSeedWeight] = useState<number>(0);

  const [allFodderTypes, setAllFodderTypes] = useState<IFodderSpecie[]>([]);

  const [changes, setChanges] = useState<any>();

  useEffect(() => {
    getPlantingTypes();
    getPlantingSpecies();
    getSeedTypes();
  }, []);

  const getPlantingTypes = async () => {
    await axios
      .get("/tasks/planting/types")
      .then((response) => {
        setPlantingTypes(
          response.data.map((type: { name: string; id: number }) => ({
            label: type.name,
            value: type.id,
          }))
        );
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const getPlantingSpecies = async () => {
    await axios
      .get("/tasks/pasture-evaluation/fodder/species")
      .then((response) => {
        const fodderTypesData = response.data.fodder_genus;
        setAllFodderTypes(fodderTypesData);

        setFodderSpecies(
          fodderTypesData.map((type: { genus_name: string; id: number }) => ({
            label: type.genus_name,
            value: type.id,
          }))
        );
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const getSeedTypes = async () => {
    await axios
      .get("/tasks/planting/seed-types")
      .then((response) => {
        const seedTypesData = response.data;

        setSeedTypes(
          seedTypesData.map((type: { name: string; id: number }) => ({
            label: type.name,
            value: type.id,
          }))
        );
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const handlePlantingTypeChoice = (opt: IOptionProps | null) => {
    setSelectedPlantingType(opt);
    const plantingTypeId = opt?.value;
    setChanges((prev: any) => ({
      ...prev,
      planting_type_id: plantingTypeId,
      fodder_species_id: undefined,
      seed_type_id: undefined,
      seed_quantity_kg: undefined,
    }));
    setShowSpecies(!!plantingTypeId);
    setSelectedFodderSpecies(null);
    setSelectedFodderType(null);
    setSelectedSeedType(null);
    setVcPercentage(0);
    setSeedWeight(0);
    setAllowTaskCreation(false);
    setShowTypes(false);
    setShowSeedTypes(false);
    setShowCounters(false);
  };

  const handleFodderSpecieChoice = (opt: IOptionProps | null) => {
    setSelectedFodderSpecies(opt);
    setSelectedFodderType(null);
    setSelectedSeedType(null);
    setVcPercentage(0);
    setSeedWeight(0);
    setAllowTaskCreation(false);
    setShowSeedTypes(false);
    setShowCounters(false);
    const fodderSpecieId = opt?.value;
    const fodderSpecie = allFodderTypes.find(
      (type) => type.id === fodderSpecieId
    );
    setChanges((prev: any) => ({
      ...prev,
      fodder_species_id: undefined,
      seed_type_id: undefined,
      seed_quantity_kg: undefined,
    }));

    if (!fodderSpecie) {
      setFodderTypes([]);
      setShowTypes(false);
      return;
    }
    const fodderTypeOptions = fodderSpecie.fodder_types.map(
      (type: IFodderType) => ({
        label: type.fodder_species,
        value: type.fodder_species_id,
      })
    );
    setFodderTypes(fodderTypeOptions);
    setShowTypes(true);
  };

  const handleFodderTypeChange = (opt: IOptionProps | null) => {
    setSelectedFodderType(opt);
    setSelectedSeedType(null);
    setVcPercentage(0);
    setSeedWeight(0);
    setAllowTaskCreation(false);
    const fodderTypeId = opt?.value;
    setChanges((prev: any) => ({
      ...prev,
      fodder_species_id: fodderTypeId,
      seed_type_id: undefined,
      seed_quantity_kg: undefined,
    }));
    setShowCounters(false);
    setShowSeedTypes(!!fodderTypeId);
  };

  const handleSeedTypeChange = (opt: IOptionProps | null) => {
    setSelectedSeedType(opt);
    setVcPercentage(0);
    setSeedWeight(0);
    setAllowTaskCreation(false);
    const seedTypeId = opt?.value;
    setChanges((prev: any) => ({
      ...prev,
      seed_type_id: seedTypeId,
      seed_quantity_kg: undefined,
    }));
    setShowCounters(!!seedTypeId);
  };

  const handleVCPercentageChange = (value: number) => {
    if (value > 100) setVcPercentage(100);
    else setVcPercentage(value);
  };

  const handleSeedWeightChange = (value: number) => {
    setSeedWeight(value);
    setChanges((prev: any) => ({ ...prev, seed_quantity_kg: value }));
  };

  useEffect(() => {
    if (seedWeight === 0 || vcPercentage === 0 || vcPercentage > 100)
      return setAllowTaskCreation(false);

    const reqChanges = {
      ...changes,
      micro_area_id: microAreaId,
      assigned_user_id: responsibleId,
    };

    const reqPayload = {
      ...payload,
      changes: [reqChanges],
    };

    setPayload(reqPayload);

    setAllowTaskCreation(true);
  }, [seedWeight, vcPercentage]);

  useEffect(() => {
    if (vcPercentage > 100) return setVcPercentage(100);

    setChanges((prev: any) => ({ ...prev, vc_percentage: vcPercentage }));
  }, [vcPercentage]);

  return (
    <>
      <div className="seed-dropdown-container">
        <DropdownV2
          name="planting-type"
          placeholder="Selecionar o tipo de plantio..."
          label="Tipo de plantio"
          options={plantingTypes}
          value={selectedPlantingType}
          menuMount="overlay"
          onChange={(opt) => handlePlantingTypeChoice(opt)}
        />
      </div>
      {showSpecies && (
        <>
          <div className="seed-dropdown-container">
            <DropdownV2
              name="planting-fodder-type"
              placeholder="Selecionar gênero..."
              label="Gênero"
              options={fodderSpecies}
              value={selectedFodderSpecies}
              menuMount="overlay"
              onChange={(opt) => handleFodderSpecieChoice(opt)}
            />
          </div>
          {showTypes && (
            <>
              <div className="seed-dropdown-container">
                <DropdownV2
                  name="planting-fodder-species"
                  placeholder="Selecionar capim..."
                  label="Capim"
                  options={fodderTypes}
                  value={selectedFodderType}
                  menuMount="overlay"
                  onChange={(opt) => handleFodderTypeChange(opt)}
                />
              </div>
              {showSeedTypes && (
                <>
                  <div className="seed-dropdown-container">
                    <DropdownV2
                      name="planting-seed-type"
                      placeholder="Selecionar tipo de sementes..."
                      label="Tipo de sementes"
                      options={seedTypes}
                      value={selectedSeedType}
                      menuMount="overlay"
                      onChange={(opt) => handleSeedTypeChange(opt)}
                    />
                  </div>
                  {showCounters && (
                    <>
                      <div className="balloon-container">
                        <MessageBalloon text="Qual o valor cultural (VC) da semente?" />
                      </div>
                      <CounterInputV2
                        name="planting-vc"
                        suffix="%"
                        value={vcPercentage}
                        min={0}
                        max={100}
                        onChange={(v) => handleVCPercentageChange(v)}
                      />
                      <div className="balloon-container">
                        <MessageBalloon text="Qual a quantidade de semente em quilos por hectare?" />
                      </div>
                      <div className="seed-dropdown-container">
                        <CounterInputV2
                          name="planting-seed-kg"
                          suffix="kg/ha"
                          value={seedWeight}
                          min={0}
                          max={99999999}
                          onChange={(v) => handleSeedWeightChange(v)}
                        />
                      </div>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}
    </>
  );
};
